import type { PageChangeFn } from '../../../components/Pagination/Pagination';
import type { ListingPagination } from '../../../hooks/api/hook-builders/listing-hook-builder';
import type {
  ContentTypeDefinition,
  SearchItem,
} from '../../../lib/flotiq-client/types/api-response';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { getObjectTitle, getTestProps } from '../../../lib/helpers';

import Pagination from '../../../components/Pagination/Pagination';
import Panel from '../../../components/Panel/Panel';

import useSelectedSpace from '../../../hooks/useSelectedSpace';

interface ContentObjectBacklinksProps {
  /**
   * An array of backlink search results
   */
  backlinks?: SearchItem[];
  /**
   * content types from backlinks indexed by name
   */
  contentTypes?: Record<string, ContentTypeDefinition>;
  /**
   * A pagination object
   */
  pagination?: ListingPagination;
  /**
   * Callback for pagination page change
   */
  onPageChange?: PageChangeFn;
  /**
   * Additional css classes
   */
  additionalClasses?: string;
  /**
   * Test id
   */
  testId?: string;
}

const ContentObjectBacklinks = ({
  backlinks,
  contentTypes,
  pagination,
  onPageChange,
  additionalClasses,
  testId = '',
}: ContentObjectBacklinksProps) => {
  const { t } = useTranslation();
  const { buildUrlWithSpace } = useSelectedSpace();

  return (
    <Panel
      title={t('ContentForm.Backlinks')}
      isCollapsable
      additionalContainerClasses={twMerge('px-4', additionalClasses)}
      additionalTitleClasses="text-lg"
      additionalChildrenClasses={'pt-2'}
      open
      {...getTestProps(testId, '', 'testId')}
    >
      <div className="flex flex-col space-y-2">
        {backlinks?.map(({ item }) => (
          <Link
            key={`${item.internal.contentType}-${item.id}`}
            className={twMerge(
              'px-3 md:px-2 xl:px-3 py-2',
              'hover:bg-slate-50 dark:hover:bg-slate-900',
              'hover:rounded-md hover:no-underline hover:text-slate-700',
              'dark:hover:text-white',
            )}
            to={
              item.internal.contentType === '_media'
                ? buildUrlWithSpace(`media/edit/${item.id}`)
                : buildUrlWithSpace(
                    `content-type-objects/edit/${item.internal.contentType}/${item.id}`,
                  )
            }
          >
            <span className="text-sm text-slate-700 dark:text-slate-200 font-bold block">
              {item.internal.contentType === '_media'
                ? t('Global.Media')
                : contentTypes?.[item.internal.contentType]?.label ||
                  item.internal.contentType}
            </span>
            {getObjectTitle(item, contentTypes?.[item.internal.contentType])}
          </Link>
        ))}
        {pagination?.total_pages && pagination.total_pages > 1 && (
          <Pagination
            numOfPages={pagination.total_pages}
            onPageChange={onPageChange}
            page={pagination.current_page}
            {...getTestProps(testId, 'pagination', 'testId')}
          />
        )}
      </div>
    </Panel>
  );
};

export default ContentObjectBacklinks;
